import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [csrfToken, setCsrfToken] = useState(null);

    const fetchCsrfToken = async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_API + '/csrf-token', { withCredentials: true });
            return response.data.token;
        } catch (error) {
            console.error('Error fetching CSRF token:', error);
        }
    };

    const loginUser = async (phone, password) => {
        if (!phone || !password) {
            throw new Error('Phone number and password are required.');
        }

        let token = csrfToken
        if (!token) {
            token = await fetchCsrfToken();
            setToken(token)
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_API}/admin/login`, {
                email: phone.trim(),
                password: password.trim(),
            }, {
                withCredentials: true,
                headers: {
                    'X-CSRF-TOKEN': token
                }
            });

            setUser(response.data.user);
            return response.data;
        } catch (error) {
            throw new Error(error?.response?.data?.message || 'Unable to log in');
        }
    };

    const isFirstRun = useRef(true)
    const fetchUser = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API}/admin/account`, {
                withCredentials: true,
            });

            setUser(response.data);

        } catch (error) {
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isFirstRun.current) {
            fetchUser();
            isFirstRun.current = false;
        }
    }, []);

    const setToken = async (token) => {
        setCsrfToken(token);

        const timerId = setTimeout(() => {
            setToken(null);
        }, 1000 * 60 * 5);

        return () => clearTimeout(timerId);
    };

    const fetchData = async (path) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API}${path}`, {
                withCredentials: true,
            });

            return response.data;
        } catch (error) {
            if (error?.response?.data?.logout === true) {
                setUser(null);
                window.location.href = "/#/user/login";
            }

            throw new Error(error?.response?.data?.message || 'Failed to fetch data');
        }
    }

    const postData = async (path, data) => {
        let token = csrfToken
        if (!token) {
            token = await fetchCsrfToken();
            setToken(token)
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_API}${path}`, data, {
                withCredentials: true,
                headers: {
                    'X-CSRF-TOKEN': token
                }
            });

            return response.data;
        } catch (error) {
            if (error?.response?.data?.logout === true) {
                setUser(null);
                window.location.href = "/#/user/login";
            }

            throw new Error(error?.response?.data?.message || 'Failed to fetch data')
        }
    }

    const value = { user, loginUser, isAuthenticated: !!user, isLoading, fetchData, postData };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider